// @ts-check
/**
 * SHIP-06: review a reviewed coding result file by file. A file list with
 * Previous and Next (and `j`/`k` on the keyboard), a "Viewed" mark per file
 * that is kept per person, and the test receipts: each command the run
 * executed, its exit code and the end of its output.
 */
import { button } from "./ui/button.js";
import { h, uid } from "./ui/dom.js";
import { badge } from "./ui/status.js";

/** @param {{ encoding: string, path: string, before: string | null, after: string | null }} file */
export function codingFileDiff(file) {
  if (file.encoding !== "utf8")
    return "Binary change. Text preview is unavailable.";
  const before = file.before === null ? [] : file.before.split("\n"),
    after = file.after === null ? [] : file.after.split("\n");
  let start = 0,
    end = 0;
  while (
    start < before.length &&
    start < after.length &&
    before[start] === after[start]
  )
    start++;
  while (
    end < before.length - start &&
    end < after.length - start &&
    before.at(-1 - end) === after.at(-1 - end)
  )
    end++;
  if (start === before.length && start === after.length)
    return "No content changes.";
  const context = Math.max(0, start - 3);
  return [
    `--- ${file.before === null ? "/dev/null" : file.path}`,
    `+++ ${file.after === null ? "/dev/null" : file.path}`,
    `@@ -${context + 1},${before.length - context - Math.max(0, end - 3)} +${context + 1},${after.length - context - Math.max(0, end - 3)} @@`,
    ...before.slice(context, start).map((line) => ` ${line}`),
    ...before.slice(start, before.length - end).map((line) => `-${line}`),
    ...after.slice(start, after.length - end).map((line) => `+${line}`),
    ...after
      .slice(after.length - end, after.length - Math.max(0, end - 3))
      .map((line) => ` ${line}`),
  ].join("\n");
}

/** @type {Record<string, string>} */
const changeWords = {
  added: "Added",
  modified: "Modified",
  deleted: "Deleted",
};
/** @param {any} file */
const changeOf = (file) =>
  file.change ||
  (file.before === null
    ? "added"
    : file.after === null
      ? "deleted"
      : "modified");

/**
 * The test receipt panel: commands, exit codes and bounded output.
 * @param {{ receipts: any[], tests?: { ran: number, failed: number } }} review
 */
export function testReceiptPanel({ receipts, tests }) {
  const failed = tests?.failed ?? 0;
  const ran = tests?.ran ?? receipts.length;
  const panel = h(
    "section",
    { class: "test-receipts", aria: { label: "Test receipts" } },
    h(
      "div",
      { class: "test-receipts__heading" },
      h("h3", { text: "Test receipts" }),
      receipts.length
        ? badge(
            failed
              ? `${failed} of ${ran} failed`
              : `${ran} ${ran === 1 ? "command" : "commands"} passed`,
            failed ? "failed" : "working",
          )
        : badge("No commands ran", "waiting"),
    ),
  );
  if (!receipts.length) {
    panel.append(
      h("p", {
        class: "muted",
        text: "The run executed no command, so nothing shows the change works. Check it by reading the diff.",
      }),
    );
    return panel;
  }
  const list = h("ol", { class: "test-receipts__list" });
  for (const receipt of receipts) {
    const passed = receipt.exitCode === 0;
    list.append(
      h(
        "li",
        {
          class: "test-receipt",
          dataset: {
            outcome:
              receipt.exitCode === null ? "none" : passed ? "pass" : "fail",
          },
        },
        h(
          "div",
          { class: "test-receipt__line" },
          h("code", { class: "test-receipt__command", text: receipt.command }),
          receipt.exitCode === null
            ? badge(`Not finished (${receipt.status})`, "waiting")
            : badge(`Exit ${receipt.exitCode}`, passed ? "working" : "failed"),
        ),
        receipt.output
          ? h("pre", { class: "test-receipt__output", text: receipt.output })
          : h("p", { class: "muted", text: "No output recorded." }),
        receipt.truncated &&
          h("p", {
            class: "muted",
            text: `Showing the last ${receipt.output.length.toLocaleString()} of ${receipt.outputCharacters.toLocaleString()} characters.`,
          }),
      ),
    );
  }
  panel.append(list);
  return panel;
}

/**
 * @param {{
 *   onViewed?: (path: string, viewed: boolean) => Promise<any> | void,
 *   onError?: (error: unknown) => void,
 * }} [options]
 */
export function createChangeReview(options = {}) {
  /** @type {any[]} */
  let files = [];
  let index = 0;
  const headingId = uid("change-review");
  const summary = h("span", { class: "change-review__summary" });
  const previous = button({
    text: "Previous file",
    icon: "chevron-left",
    size: "sm",
    onClick: () => move(-1),
  });
  const next = button({
    text: "Next file",
    iconEnd: "chevron-right",
    size: "sm",
    onClick: () => move(1),
  });
  const list = h("ol", {
    class: "change-review__files",
    aria: { label: "Changed files" },
  });
  const current = h("div", { class: "change-review__file" });
  const receipts = h("div");
  const element = h(
    "section",
    {
      class: "change-review",
      aria: { labelledby: headingId },
      tabindex: "-1",
      dataset: { reviewKeys: "j-k" },
    },
    h(
      "div",
      { class: "change-review__heading" },
      h("h3", { id: headingId, text: "Changed files" }),
      summary,
      h("div", { class: "change-review__nav" }, previous, next),
    ),
    h("p", {
      class: "muted change-review__hint",
      text: "Press j for the next file and k for the previous one.",
    }),
    h("div", { class: "change-review__body" }, list, current),
    receipts,
  );
  element.addEventListener("keydown", (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const target = /** @type {HTMLElement} */ (event.target);
    if (
      /^(INPUT|TEXTAREA|SELECT)$/u.test(target.tagName) &&
      target.getAttribute("type") !== "checkbox"
    )
      return;
    if (target.isContentEditable) return;
    if (event.key === "j" || event.key === "k") {
      event.preventDefault();
      move(event.key === "j" ? 1 : -1, true);
    }
  });
  /** @param {number} step @param {boolean} [keyboard] */
  function move(step, keyboard = false) {
    if (!files.length) return;
    const target = Math.min(files.length - 1, Math.max(0, index + step));
    select(target, keyboard);
  }
  /** @param {number} target @param {boolean} [focusRow] */
  function select(target, focusRow = false) {
    index = target;
    render();
    if (focusRow)
      /** @type {HTMLElement | null} */ (
        list.querySelector('[aria-current="true"]')
      )?.focus();
  }
  /** @param {any} file @param {boolean} viewed */
  async function mark(file, viewed) {
    const before = file.viewed;
    file.viewed = viewed;
    render();
    try {
      await options.onViewed?.(file.path, viewed);
    } catch (error) {
      file.viewed = before;
      render();
      options.onError?.(error);
    }
  }
  /** Redraw, keeping keyboard focus on the same control (j/k keep working). */
  function render() {
    const active = document.activeElement;
    const where =
      active && current.contains(active) && active.tagName === "INPUT"
        ? "viewed"
        : active && list.contains(active)
          ? "row"
          : null;
    draw();
    if (where === "viewed")
      /** @type {HTMLElement | null} */ (
        current.querySelector("input")
      )?.focus();
    else if (where === "row")
      /** @type {HTMLElement | null} */ (
        list.querySelector('[aria-current="true"]')
      )?.focus();
  }
  function draw() {
    const viewed = files.filter((file) => file.viewed).length;
    summary.textContent = files.length
      ? `${files.length} ${files.length === 1 ? "file" : "files"} · ${viewed} viewed`
      : "No changed files";
    previous.disabled = index <= 0;
    next.disabled = index >= files.length - 1;
    list.replaceChildren(
      ...files.map((file, position) => {
        const change = changeOf(file);
        return h(
          "li",
          {
            class: "change-review__item",
            dataset: { viewed: String(!!file.viewed) },
          },
          h(
            "button",
            {
              type: "button",
              class: "change-review__row",
              aria: {
                current: position === index ? "true" : undefined,
                label: `${file.path}, ${changeWords[change].toLowerCase()}${file.viewed ? ", viewed" : ""}`,
              },
              onClick: () => select(position),
            },
            h("span", { class: "change-review__path", text: file.path }),
            badge(
              changeWords[change],
              change === "deleted"
                ? "failed"
                : change === "added"
                  ? "working"
                  : "info",
            ),
            file.viewed && badge("Viewed", "neutral"),
          ),
        );
      }),
    );
    const file = files[index];
    if (!file) {
      current.replaceChildren(
        h("p", { class: "muted", text: "This result changed no files." }),
      );
      return;
    }
    const checkboxId = uid("viewed");
    const checkbox = h("input", {
      type: "checkbox",
      id: checkboxId,
      checked: !!file.viewed,
      onChange: (/** @type {Event} */ event) =>
        void mark(file, /** @type {HTMLInputElement} */ (event.target).checked),
    });
    current.replaceChildren(
      h(
        "div",
        { class: "change-review__file-heading" },
        h("h4", {
          class: "change-review__file-title",
          text: `${index + 1} of ${files.length} · ${file.path}`,
        }),
        h(
          "label",
          { class: "change-review__viewed", for: checkboxId },
          checkbox,
          " Viewed",
        ),
      ),
      h("pre", {
        class: "change-review__diff",
        tabindex: "0",
        aria: { label: `Diff of ${file.path}` },
        text: codingFileDiff(file),
      }),
    );
  }
  return {
    element,
    /**
     * @param {{ files: any[], receipts?: any[], tests?: { ran: number, failed: number } }} review
     */
    setData(review) {
      const path = files[index]?.path;
      files = review.files.map((file) => ({ ...file }));
      const kept = files.findIndex((file) => file.path === path);
      index = kept >= 0 ? kept : 0;
      render();
      receipts.replaceChildren(
        ...(review.receipts
          ? [
              testReceiptPanel({
                receipts: review.receipts,
                tests: review.tests,
              }),
            ]
          : []),
      );
    },
    /** Redraw (after a host disabled every button while it was busy). */
    refresh() {
      render();
    },
    get index() {
      return index;
    },
    focus() {
      element.focus();
    },
  };
}
